import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import SendIcon from "@mui/icons-material/Send";
import ImageIcon from "@mui/icons-material/Image";
import CircularProgress from "@mui/material/CircularProgress";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  getLinkedInAuthUrl,
  getLinkedInConnectionStatus,
  disconnectLinkedInAccount,
  publishLinkedInImagePost,
  getInstagramAuthUrl,
  getInstagramConnectionStatus,
  disconnectInstagramAccount,
  publishInstagramImagePost,
} from "../../../services/business.service";

type Platform = "LinkedIn" | "Instagram" | "Facebook";

interface PostedSocialPost {
  id: string;
  caption: string;
  image: string;
  platforms: Platform[];
  postedAt: string;
}

const PLATFORM_ICONS: Record<Platform, React.ReactNode> = {
  LinkedIn: <LinkedInIcon fontSize="small" />,
  Instagram: <InstagramIcon fontSize="small" />,
  Facebook: <FacebookIcon fontSize="small" />,
};

const PLATFORM_COLORS: Record<Platform, string> = {
  LinkedIn: "#0A66C2",
  Instagram: "#C13584",
  Facebook: "#1877F2",
};

const DEFAULT_PLATFORMS: Platform[] = ["LinkedIn", "Instagram", "Facebook"];

const getDraftUrl = (platform: Platform, captionText: string) => {
  const encodedCaption = encodeURIComponent(captionText);
  const shareUrl = encodeURIComponent(window.location.origin);

  switch (platform) {
    case "LinkedIn":
      return "https://www.linkedin.com/feed/?shareActive=true";
    case "Facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${encodedCaption}`;
    case "Instagram":
      return "https://www.instagram.com/create/select/";
    default:
      return "";
  }
};

const SocialPosts: React.FC = () => {
  const [caption, setCaption] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>(DEFAULT_PLATFORMS);
  const [posts, setPosts] = useState<PostedSocialPost[]>([]);
  const [isPosting, setIsPosting] = useState(false);
  const [isConnectingLinkedIn, setIsConnectingLinkedIn] = useState(false);
  const [isDisconnectingLinkedIn, setIsDisconnectingLinkedIn] = useState(false);
  const [isConnectingInstagram, setIsConnectingInstagram] = useState(false);
  const [isDisconnectingInstagram, setIsDisconnectingInstagram] = useState(false);
  const [linkedInStatus, setLinkedInStatus] = useState({
    connected: false,
    displayName: "",
  });
  const [instagramStatus, setInstagramStatus] = useState({
    connected: false,
    displayName: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const canPost = useMemo(
    () => Boolean(imagePreview && selectedPlatforms.length),
    [imagePreview, selectedPlatforms]
  );

  const showSnackbar = (severity: "success" | "error", message: string) => {
    setSnackbar({ open: true, severity, message });
  };

  useEffect(() => {
    const loadSocialStatuses = async () => {
      const [status, instagram] = await Promise.all([
        getLinkedInConnectionStatus(),
        getInstagramConnectionStatus(),
      ]);
      setLinkedInStatus({
        connected: status.connected,
        displayName: status.displayName || "",
      });
      setInstagramStatus({
        connected: instagram.connected,
        displayName: instagram.displayName || "",
      });

      const urlParams = new URLSearchParams(window.location.search);
      const linkedInError = urlParams.get("linkedin_error");
      const instagramError = urlParams.get("instagram_error");

      if (urlParams.get("linkedin") === "connected") {
        showSnackbar("success", "LinkedIn connected successfully.");
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (urlParams.get("instagram") === "connected") {
        showSnackbar("success", "Instagram connected successfully.");
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (linkedInError) {
        showSnackbar("error", linkedInError);
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (instagramError) {
        showSnackbar("error", instagramError);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    loadSocialStatuses();
  }, []);

  const handleConnectLinkedIn = async () => {
    setIsConnectingLinkedIn(true);
    try {
      const response = await getLinkedInAuthUrl();
      if (!response?.url) {
        showSnackbar("error", "Unable to start LinkedIn authorization.");
        return;
      }

      window.location.href = response.url;
    } finally {
      setIsConnectingLinkedIn(false);
    }
  };

  const handleDisconnectLinkedIn = async () => {
    setIsDisconnectingLinkedIn(true);
    try {
      const response = await disconnectLinkedInAccount();
      setLinkedInStatus({
        connected: response.connected,
        displayName: response.displayName || "",
      });
      showSnackbar("success", "LinkedIn account disconnected.");
    } finally {
      setIsDisconnectingLinkedIn(false);
    }
  };

  const handleConnectInstagram = async () => {
    setIsConnectingInstagram(true);
    try {
      const response = await getInstagramAuthUrl();
      if (!response?.url) {
        showSnackbar("error", "Unable to start Instagram authorization.");
        return;
      }

      window.location.href = response.url;
    } finally {
      setIsConnectingInstagram(false);
    }
  };

  const handleDisconnectInstagram = async () => {
    setIsDisconnectingInstagram(true);
    try {
      const response = await disconnectInstagramAccount();
      setInstagramStatus({
        connected: response.connected,
        displayName: response.displayName || "",
      });
      showSnackbar("success", "Instagram account disconnected.");
    } finally {
      setIsDisconnectingInstagram(false);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showSnackbar("error", "Please upload an image file.");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const handlePlatformToggle = (platform: Platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((item) => item !== platform)
        : [...prev, platform]
    );
  };

  const copyCaptionToClipboard = async (captionText: string) => {
    if (!captionText || !navigator.clipboard?.writeText) return false;

    try {
      await navigator.clipboard.writeText(captionText);
      return true;
    } catch {
      return false;
    }
  };

  const handlePost = async () => {
    if (!imagePreview || !imageFile) {
      showSnackbar("error", "Please upload an image before posting.");
      return;
    }

    if (!selectedPlatforms.length) {
      showSnackbar("error", "Select at least one social platform.");
      return;
    }

    const trimmedCaption = caption.trim();

    if (selectedPlatforms.includes("LinkedIn") && !linkedInStatus.connected) {
      showSnackbar("error", "Connect LinkedIn before publishing a LinkedIn post.");
      return;
    }

    if (selectedPlatforms.includes("Instagram") && !instagramStatus.connected) {
      showSnackbar("error", "Connect Instagram before publishing an Instagram post.");
      return;
    }

    setIsPosting(true);

    try {
      let linkedInPublished = false;
      let instagramPublished = false;
      let postedImageUrl = imagePreview;

      if (selectedPlatforms.includes("LinkedIn")) {
        const linkedInResult = await publishLinkedInImagePost(imageFile, trimmedCaption);
        linkedInPublished = Boolean(linkedInResult?.linkedInPostId || linkedInResult?.linkedInAsset);
        postedImageUrl = linkedInResult?.imageUrl || postedImageUrl;

        if (!linkedInPublished) {
          showSnackbar("error", "LinkedIn post failed. Please check LinkedIn configuration.");
          return;
        }
      }

      if (selectedPlatforms.includes("Instagram")) {
        const instagramResult = await publishInstagramImagePost(imageFile, trimmedCaption);
        instagramPublished = Boolean(instagramResult?.instagramPostId);
        postedImageUrl = instagramResult?.imageUrl || postedImageUrl;

        if (!instagramPublished) {
          showSnackbar("error", "Instagram post failed. Please check Instagram configuration.");
          return;
        }
      }

      selectedPlatforms
        .filter((platform) => platform !== "LinkedIn" && platform !== "Instagram")
        .forEach((platform) => {
          window.open(getDraftUrl(platform, trimmedCaption), "_blank", "noopener,noreferrer");
        });

      const shouldCopyCaption = selectedPlatforms.some(
        (platform) => platform !== "LinkedIn" && platform !== "Instagram"
      );
      const captionCopied = shouldCopyCaption
        ? await copyCaptionToClipboard(trimmedCaption)
        : false;

      const newPost: PostedSocialPost = {
        id: `${Date.now()}`,
        caption: trimmedCaption,
        image: postedImageUrl,
        platforms: selectedPlatforms,
        postedAt: new Date().toISOString(),
      };

      setPosts((prevPosts) => [newPost, ...prevPosts]);
      setCaption("");
      setImagePreview("");
      setImageFile(null);
      setSelectedPlatforms(DEFAULT_PLATFORMS);
      showSnackbar(
        "success",
        linkedInPublished || instagramPublished
          ? "Selected connected social posts published successfully."
          : captionCopied
            ? "Social draft opened. Caption copied to clipboard."
            : "Social draft opened."
      );
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1f2937" }}>
            Social Media Posts
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
            Post business images to LinkedIn, Instagram, and Facebook.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            gap={2}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box sx={{ color: linkedInStatus.connected ? "#16a34a" : "#0A66C2", display: "flex" }}>
                {linkedInStatus.connected ? <CheckCircleIcon /> : <LinkedInIcon />}
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  LinkedIn {linkedInStatus.connected ? "Connected" : "Not Connected"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  {linkedInStatus.connected
                    ? linkedInStatus.displayName || "Ready to publish image posts."
                    : "Connect once to publish image posts directly."}
                </Typography>
              </Box>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
              {linkedInStatus.connected && (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={isDisconnectingLinkedIn}
                  onClick={handleDisconnectLinkedIn}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  {isDisconnectingLinkedIn ? "Disconnecting..." : "Disconnect"}
                </Button>
              )}
              <Button
                variant={linkedInStatus.connected ? "outlined" : "contained"}
                startIcon={isConnectingLinkedIn ? <CircularProgress color="inherit" size={18} /> : <LinkIcon />}
                disabled={isConnectingLinkedIn || isDisconnectingLinkedIn}
                onClick={handleConnectLinkedIn}
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                {linkedInStatus.connected ? "Connect Different" : "Connect LinkedIn"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            gap={2}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box sx={{ color: instagramStatus.connected ? "#16a34a" : "#C13584", display: "flex" }}>
                {instagramStatus.connected ? <CheckCircleIcon /> : <InstagramIcon />}
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Instagram {instagramStatus.connected ? "Connected" : "Not Connected"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  {instagramStatus.connected
                    ? instagramStatus.displayName || "Ready to publish image posts."
                    : "Connect a Meta account with an Instagram Business account."}
                </Typography>
              </Box>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
              {instagramStatus.connected && (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={isDisconnectingInstagram}
                  onClick={handleDisconnectInstagram}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  {isDisconnectingInstagram ? "Disconnecting..." : "Disconnect"}
                </Button>
              )}
              <Button
                variant={instagramStatus.connected ? "outlined" : "contained"}
                startIcon={isConnectingInstagram ? <CircularProgress color="inherit" size={18} /> : <LinkIcon />}
                disabled={isConnectingInstagram || isDisconnectingInstagram}
                onClick={handleConnectInstagram}
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                {instagramStatus.connected ? "Connect Different" : "Connect Instagram"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(360px, 5fr) 7fr" },
            gap: 3,
          }}
        >
          <Box>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                bgcolor: "#fff",
              }}
            >
              <Stack spacing={2.5}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Create Post
                </Typography>

                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<AddPhotoAlternateIcon />}
                  sx={{
                    borderRadius: 2,
                    justifyContent: "center",
                    py: 1.2,
                    borderStyle: "dashed",
                    fontWeight: 700,
                  }}
                >
                  Upload Image
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleImageChange}
                  />
                </Button>

                <Box
                  sx={{
                    minHeight: 260,
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    bgcolor: "#f9fafb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {imagePreview ? (
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Post preview"
                      sx={{
                        width: "100%",
                        height: 260,
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Stack alignItems="center" spacing={1} sx={{ color: "#9ca3af" }}>
                      <ImageIcon sx={{ fontSize: 48 }} />
                      <Typography variant="body2">Image preview</Typography>
                    </Stack>
                  )}
                </Box>

                <TextField
                  label="Caption"
                  multiline
                  minRows={4}
                  value={caption}
                  onChange={(event) => setCaption(event.target.value)}
                  placeholder="Write a caption for your post"
                  fullWidth
                />

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Platforms
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {DEFAULT_PLATFORMS.map((platform) => (
                      <FormControlLabel
                        key={platform}
                        control={
                          <Checkbox
                            checked={selectedPlatforms.includes(platform)}
                            onChange={() => handlePlatformToggle(platform)}
                          />
                        }
                        label={
                          <Stack direction="row" alignItems="center" gap={0.75}>
                            <Box sx={{ color: PLATFORM_COLORS[platform], display: "flex" }}>
                              {PLATFORM_ICONS[platform]}
                            </Box>
                            <span>{platform}</span>
                          </Stack>
                        }
                        sx={{ mr: 1 }}
                      />
                    ))}
                  </Stack>
                </Box>

                <Button
                  variant="contained"
                  startIcon={isPosting ? <CircularProgress color="inherit" size={18} /> : <SendIcon />}
                  disabled={!canPost || isPosting}
                  onClick={handlePost}
                  sx={{ borderRadius: 2, py: 1, fontWeight: 700 }}
                >
                  {isPosting ? "Posting..." : "Share Post"}
                </Button>
              </Stack>
            </Paper>
          </Box>

          <Box>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                bgcolor: "#fff",
                minHeight: "100%",
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Already Posted
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280" }}>
                    Recent posts created from this browser.
                  </Typography>
                </Box>
                <Divider />

                {posts.length === 0 ? (
                  <Box
                    sx={{
                      py: 10,
                      textAlign: "center",
                      color: "#6b7280",
                      bgcolor: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: 2,
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 46, color: "#9ca3af", mb: 1 }} />
                    <Typography variant="h6" sx={{ color: "#374151" }}>
                      No posts yet
                    </Typography>
                    <Typography variant="body2">
                      Upload an image and post it to see it here.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 2,
                    }}
                  >
                    {posts.map((post) => (
                      <Box key={post.id}>
                        <Card
                          elevation={0}
                          sx={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 2,
                            overflow: "hidden",
                            height: "100%",
                          }}
                        >
                          <Box
                            component="img"
                            src={post.image}
                            alt="Posted social media"
                            sx={{
                              width: "100%",
                              height: 190,
                              objectFit: "cover",
                              bgcolor: "#f3f4f6",
                            }}
                          />
                          <CardContent>
                            <Stack spacing={1.5}>
                              <Stack direction="row" flexWrap="wrap" gap={1}>
                                {post.platforms.map((platform) => (
                                  <Chip
                                    key={platform}
                                    icon={PLATFORM_ICONS[platform] as React.ReactElement}
                                    label={platform}
                                    size="small"
                                    sx={{
                                      borderRadius: 1.5,
                                      color: PLATFORM_COLORS[platform],
                                      bgcolor: "#f9fafb",
                                      fontWeight: 700,
                                    }}
                                  />
                                ))}
                              </Stack>
                              {post.caption && (
                                <Typography variant="body2" sx={{ color: "#374151" }}>
                                  {post.caption}
                                </Typography>
                              )}
                              <Typography variant="caption" sx={{ color: "#6b7280" }}>
                                Posted {new Date(post.postedAt).toLocaleString()}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Box>
                    ))}
                  </Box>
                )}
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SocialPosts;
